import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { api } from '../lib/api';
import { clearToken, getToken } from '../lib/auth';
import { backendRoleToUiRole, UiRole } from '../lib/roles';
import { ensurePushRegisteredIfGranted } from '../lib/push';

export interface BackendMe {
  id: number;
  email: string;
  full_name: string;
  role: string;
  over16?: boolean;
  is_platform_admin?: boolean;
  is_suspended?: boolean;
  school_id?: number | null;
  school_name?: string | null;
  school_mode?: 'b2c' | 'b2b';
  must_change_password?: boolean;
  gdpr_accepted?: boolean;
  terms_accepted?: boolean;
  privacy_accepted?: boolean;
  cookies_accepted?: boolean;
  subjects?: Array<{ id: number; name: string; classroom_id: number }>;
  titles?: string[];
  phone?: string | null;
  avatar_url?: string | null;
}

interface AuthState {
  me: BackendMe | null;
  uiRole: UiRole | null;
  isLoading: boolean;
  refresh: () => Promise<void>;
  signOut: () => void;
}

const AuthContext = createContext<AuthState | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [me, setMe] = useState<BackendMe | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const refresh = useCallback(async () => {
    const token = getToken();
    if (!token) {
      setMe(null);
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    try {
      const resp = await api.get('/api/users/me');
      setMe(resp.data);
      void ensurePushRegisteredIfGranted();
    } catch {
      clearToken();
      setMe(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const signOut = useCallback(() => {
    clearToken();
    setMe(null);
  }, []);

  const uiRole = useMemo(() => (me ? backendRoleToUiRole(me.role) : null), [me]);

  const value = useMemo<AuthState>(
    () => ({ me, uiRole, isLoading, refresh, signOut }),
    [me, uiRole, isLoading, refresh, signOut]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return ctx;
}
